import { useEffect, useRef, useState } from 'react'
import { Captions, Film, Music, Plus, Sparkles, Type, Upload } from 'lucide-react'
import { useEditor, type LeftTab } from '../store/editor'
import { APP2, listVaideoProjects, loginUrl, mediaThumbUrl, type VaideoProjectSummary } from '../lib/api'
import { formatShort, formatTime } from '../lib/model'
import { importVaideoProject, onUploads, uploadFiles, type UploadItem } from '../lib/actions'
import type { SubStyle, TextProps } from '../types'

const TABS: { key: LeftTab; label: string; icon: JSX.Element }[] = [
  { key: 'media', label: 'Medios', icon: <Film size={18} /> },
  { key: 'vaideo', label: 'Vaideo', icon: <Sparkles size={18} /> },
  { key: 'text', label: 'Texto', icon: <Type size={18} /> },
  { key: 'subtitles', label: 'Subtítulos', icon: <Captions size={18} /> },
]

export default function LeftPanel() {
  const tab = useEditor((s) => s.leftTab)
  return (
    <div className="panel left">
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`tab ${tab === t.key ? 'on' : ''}`} onClick={() => useEditor.getState().set({ leftTab: t.key })}>
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>
      <div className="tab-body">
        {tab === 'media' && <MediaTab />}
        {tab === 'vaideo' && <VaideoTab />}
        {tab === 'text' && <TextTab />}
        {tab === 'subtitles' && <SubtitlesTab />}
      </div>
    </div>
  )
}

function LoginNote({ what }: { what: string }) {
  return (
    <div className="group">
      <div>Inicia sesión en Vaideo (gratis) para {what}.</div>
      <a className="btn primary" href={loginUrl()}>Iniciar sesión</a>
    </div>
  )
}

function MediaTab() {
  const assets = useEditor((s) => s.project.assets)
  const user = useEditor((s) => s.user)
  const authChecked = useEditor((s) => s.authChecked)
  const [ups, setUps] = useState<UploadItem[]>([])
  const [over, setOver] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  useEffect(() => onUploads(setUps), [])

  return (
    <>
      <div className="section-title">Biblioteca</div>
      {authChecked && !user && <LoginNote what="subir tus vídeos, música e imágenes" />}
      <div
        className={`dropzone ${over ? 'over' : ''}`}
        onClick={() => input.current?.click()}
        onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true) } }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setOver(false)
          uploadFiles(Array.from(e.dataTransfer.files))
        }}
      >
        <Upload size={20} style={{ marginBottom: 6 }} />
        <div><b>Importar archivos</b></div>
        <div className="hint">Arrastra aquí vídeo, audio o imágenes</div>
        <input ref={input} type="file" multiple accept="video/*,audio/*,image/*" hidden
          onChange={(e) => { uploadFiles(Array.from(e.target.files ?? [])); e.target.value = '' }} />
      </div>
      <div className="assets">
        {ups.map((u) => (
          <div key={u.key} className="asset uploading">
            <div style={{ width: '100%' }}>
              <div className="hint" style={{ marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
              <div className="progress"><div style={{ width: `${Math.round(u.progress * 100)}%` }} /></div>
              <div className="hint" style={{ marginTop: 4 }}>{u.progress >= 1 ? 'Procesando…' : `${Math.round(u.progress * 100)}%`}</div>
            </div>
          </div>
        ))}
        {assets.map((a) => (
          <div
            key={a.id}
            className="asset"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('application/x-vaideo-asset', a.id)
              e.dataTransfer.effectAllowed = 'copy'
            }}
            onDoubleClick={() => useEditor.getState().addAssetClip(a.id)}
            title={`${a.name}\nDoble clic o + para añadir a la línea de tiempo`}
          >
            {a.info.kind === 'audio'
              ? <div className="audio-ph"><Music size={26} /></div>
              : <img src={mediaThumbUrl(a.id, a.info.kind === 'video' ? Math.min(1, a.info.durationSec / 2) : 0, 240)} alt="" loading="lazy" />}
            <div className="meta">
              <span>{a.name}</span>
              {a.info.kind !== 'image' && <span>{formatShort(a.info.durationSec)}</span>}
            </div>
            <button className="add" title="Añadir a la línea de tiempo" onClick={(e) => { e.stopPropagation(); useEditor.getState().addAssetClip(a.id) }}>
              <Plus size={15} />
            </button>
          </div>
        ))}
      </div>
      {!assets.length && !ups.length && <p className="hint" style={{ marginTop: 14 }}>Tus archivos aparecerán aquí. Arrástralos a la línea de tiempo.</p>}
    </>
  )
}

function VaideoTab() {
  const user = useEditor((s) => s.user)
  const authChecked = useEditor((s) => s.authChecked)
  const [items, setItems] = useState<VaideoProjectSummary[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!user) return
    listVaideoProjects().then(setItems).catch((e) => setErr(String(e.message || e)))
  }, [user])
  if (authChecked && !user) return <LoginNote what="abrir tus vídeos de Vaideo" />
  return (
    <>
      <div className="section-title">Mis vídeos de Vaideo</div>
      <p className="hint" style={{ marginTop: -4 }}>Ábrelos aquí para recortar, cambiar textos y subtítulos, añadir música y exportar.</p>
      {err && <p className="hint" style={{ color: 'var(--danger)' }}>{err}</p>}
      {!items && !err && <div className="spinner" style={{ marginTop: 20 }} />}
      <div className="list">
        {items?.map((v) => (
          <button key={v.uid} className="proj" style={{ textAlign: 'left', padding: 0, color: 'inherit' }}
            onClick={() => importVaideoProject(v.uid, { confirmReplace: true })}>
            <div className="th" style={{ backgroundImage: v.thumbnailUrl ? `url(${APP2}${v.thumbnailUrl})` : undefined }} />
            <div className="inf">
              <b>{v.title || v.uid}</b>
              <span className="hint">{v.aspectRatio ?? ''}{v.durationSec ? ` · ${formatShort(v.durationSec)}` : ''}</span>
            </div>
          </button>
        ))}
        {items && !items.length && <p className="hint">Aún no tienes vídeos terminados en Vaideo.</p>}
      </div>
      <a className="btn" style={{ marginTop: 12 }} href={`${APP2}/new`} target="_blank" rel="noreferrer"><Plus size={15} />Crear vídeo con IA</a>
    </>
  )
}

const TEXT_PRESETS: { label: string; props: Partial<TextProps> }[] = [
  { label: 'Texto', props: { text: 'Texto', fontSize: 80, bold: false } },
  { label: 'Título', props: { text: 'TÍTULO', fontSize: 140, bold: true } },
  { label: 'Impacto', props: { text: 'IMPACTO', fontSize: 120, bold: true, color: '#FFD400' } },
  { label: 'Caja negra', props: { text: 'Caja negra', fontSize: 72, bold: true, backgroundColor: '#000000cc' } },
  { label: 'Caja blanca', props: { text: 'Caja blanca', fontSize: 72, bold: true, color: '#111111', backgroundColor: '#ffffff' } },
  { label: 'Alerta', props: { text: 'ÚLTIMA HORA', fontSize: 84, bold: true, backgroundColor: '#e53935' } },
  { label: 'Cursiva', props: { text: 'Elegante', fontSize: 96, bold: false, italic: true } },
  { label: 'Menta', props: { text: 'Fresco', fontSize: 110, bold: true, color: '#3ddc97' } },
]

function presetStyle(p: Partial<TextProps>): React.CSSProperties {
  return {
    fontFamily: 'InterEditor', fontWeight: p.bold ? 700 : 400, fontStyle: p.italic ? 'italic' : 'normal',
    color: p.color ?? '#fff', background: p.backgroundColor && p.backgroundColor !== 'transparent' ? p.backgroundColor : undefined,
    fontSize: Math.max(12, Math.min(20, (p.fontSize ?? 80) / 6)), padding: p.backgroundColor ? '2px 6px' : 0,
  }
}

function TextTab() {
  return (
    <>
      <div className="section-title">Añadir texto</div>
      <div className="preset-grid">
        {TEXT_PRESETS.map((t) => (
          <button key={t.label} className="preset" onClick={() => useEditor.getState().addText(t.props)} title="Añadir en el cursor">
            <span style={presetStyle(t.props)}>{t.props.text}</span>
          </button>
        ))}
      </div>
      <p className="hint" style={{ marginTop: 12 }}>Se añade en la posición del cursor. Arrástralo en la vista previa para moverlo y usa las esquinas para cambiar su tamaño.</p>
    </>
  )
}

export const SUB_PRESETS: { label: string; style: Partial<SubStyle> }[] = [
  { label: 'Clásico', style: { color: '#ffffff', outline: true, outlineColor: '#000000', backgroundColor: 'transparent', fontWeight: 800, shadow: true } },
  { label: 'Amarillo', style: { color: '#FFE600', outline: true, outlineColor: '#000000', backgroundColor: 'transparent', fontWeight: 800, shadow: true } },
  { label: 'Caja', style: { color: '#ffffff', outline: false, backgroundColor: '#000000b3', fontWeight: 700, shadow: false } },
  { label: 'Minimal', style: { color: '#ffffff', outline: false, backgroundColor: 'transparent', fontWeight: 600, shadow: true } },
  { label: 'Menta', style: { color: '#3ddc97', outline: true, outlineColor: '#000000', backgroundColor: 'transparent', fontWeight: 800, shadow: true } },
  { label: 'Rosa', style: { color: '#ffffff', outline: false, backgroundColor: '#ff3d8b', fontWeight: 800, shadow: false } },
]

function SubtitlesTab() {
  const style = useEditor((s) => s.project.subtitleStyle)
  const subs = useEditor((s) => s.project.clips)
  const selection = useEditor((s) => s.selection)
  const list = subs.filter((c) => c.kind === 'subtitle').sort((a, b) => a.startSec - b.startSec)
  const setStyle = (patch: Partial<SubStyle>, history = true) =>
    useEditor.getState().mutate((p) => {
      p.subtitleStyle = { ...p.subtitleStyle, ...patch }
      for (const c of p.clips) if (c.kind === 'subtitle') delete c.subStyle
    }, { history })

  return (
    <>
      <div className="section-title">Estilo de subtítulos</div>
      <div className="preset-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {SUB_PRESETS.map((p) => (
          <button key={p.label} className="preset" style={{ height: 52 }} onClick={() => setStyle(p.style)}>
            <span style={{
              fontFamily: 'InterEditor', fontWeight: p.style.fontWeight, color: p.style.color, fontSize: 14,
              WebkitTextStroke: p.style.outline ? `0.6px ${p.style.outlineColor}` : undefined,
              background: p.style.backgroundColor !== 'transparent' ? p.style.backgroundColor : undefined, padding: '1px 5px',
            }}>{p.label}</span>
          </button>
        ))}
      </div>
      <div className="group" style={{ marginTop: 12 }}>
        <div className="row"><label>Tamaño</label>
          <div className="slider">
            <input type="range" min={24} max={140} value={style.fontSize}
              onPointerDown={() => useEditor.getState().beginGesture()}
              onChange={(e) => setStyle({ fontSize: Number(e.target.value) }, false)} />
            <span className="hint">{style.fontSize}</span>
          </div>
        </div>
        <div className="row"><label>Posición</label>
          <div className="seg">
            {(['top', 'middle', 'bottom'] as const).map((pos) => (
              <button key={pos} className={style.position === pos ? 'on' : ''} onClick={() => setStyle({ position: pos })}>
                {pos === 'top' ? 'Arriba' : pos === 'middle' ? 'Centro' : 'Abajo'}
              </button>
            ))}
          </div>
        </div>
        <div className="row"><label>Color</label>
          <input type="color" value={style.color.slice(0, 7)} onChange={(e) => setStyle({ color: e.target.value })} />
        </div>
        <div className="row"><label>Contorno</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={style.outline} onChange={(e) => setStyle({ outline: e.target.checked })} />
            <input type="color" value={style.outlineColor.slice(0, 7)} onChange={(e) => setStyle({ outlineColor: e.target.value, outline: true })} />
          </div>
        </div>
        <div className="row"><label>Fondo</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={style.backgroundColor !== 'transparent'}
              onChange={(e) => setStyle({ backgroundColor: e.target.checked ? '#000000b3' : 'transparent' })} />
            {style.backgroundColor !== 'transparent' && (
              <input type="color" value={style.backgroundColor.slice(0, 7)} onChange={(e) => setStyle({ backgroundColor: e.target.value + 'cc' })} />
            )}
          </div>
        </div>
      </div>

      <div className="section-title" style={{ marginTop: 16 }}>
        <span>Subtítulos ({list.length})</span>
        <button className="btn sm" onClick={() => useEditor.getState().addSubtitle()}><Plus size={13} />Añadir</button>
      </div>
      {!list.length && <p className="hint">Los vídeos de Vaideo traen los subtítulos ya colocados. También puedes añadirlos a mano en el cursor.</p>}
      <div className="list">
        {list.map((c) => (
          <div key={c.id} className={`sub-item ${selection.includes(c.id) ? 'on' : ''}`}>
            <span className="tc" onClick={() => { const s = useEditor.getState(); s.set({ playing: false }); s.setPlayhead(c.startSec + 0.001); s.select([c.id]) }}>
              {formatTime(c.startSec).slice(0, 5)}
            </span>
            <textarea
              value={c.rawText ?? ''}
              onFocus={() => { const s = useEditor.getState(); s.select([c.id]); s.set({ playing: false }); s.setPlayhead(c.startSec + 0.001); s.beginGesture() }}
              onChange={(e) => useEditor.getState().mutate((p) => {
                const x = p.clips.find((y) => y.id === c.id)
                if (x) x.rawText = e.target.value
              }, { history: false })}
              rows={2}
            />
          </div>
        ))}
      </div>
    </>
  )
}
